/**
 * AuthCallback.tsx
 * Strona odbierająca token aktywacyjny z maila Supabase.
 *
 * Supabase wysyła link w formacie:
 *   https://twoja-domena.pl/auth/callback?token_hash=xxx&type=signup
 *
 * Ponieważ używamy HashRouter, link aktywacyjny musi wskazywać
 * na stronę BEZ hash — stąd osobna trasa /auth/callback w czystym URL.
 *
 * Ta strona wywołuje verifyOtp, ustawia sesję i przekierowuje do /konto.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ROUTE_PATHS } from "@/lib/index";

type Status = "loading" | "success" | "error";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Odczytaj parametry z URL (nie z hash — Supabase wysyła je jako query params)
        const params = new URLSearchParams(window.location.search);
        const tokenHash = params.get("token_hash");
        const type = params.get("type") as "signup" | "recovery" | "email_change" | null;
        const errorParam = params.get("error");
        const errorDescription = params.get("error_description");

        // Obsłuż błąd zwrócony przez Supabase
        if (errorParam) {
          const desc = errorDescription?.replace(/\+/g, " ") ?? errorParam;
          if (errorParam === "access_denied" && desc.includes("expired")) {
            setMessage("Link aktywacyjny wygasł. Zarejestruj się ponownie, aby otrzymać nowy link.");
          } else {
            setMessage(`Błąd: ${desc}`);
          }
          setStatus("error");
          return;
        }

        // Weryfikacja tokenu
        if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
          if (error) {
            if (error.message.includes("expired")) {
              setMessage("Link aktywacyjny wygasł. Proszę zarejestrować się ponownie.");
            } else {
              setMessage(error.message);
            }
            setStatus("error");
            return;
          }
          setStatus("success");
          // Po 2s przekieruj do panelu
          setTimeout(() => navigate(ROUTE_PATHS.KONTO, { replace: true }), 2000);
          return;
        }

        // Brak tokenu — spróbuj getSession (Supabase PKCEflow może już ustawić sesję)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setStatus("success");
          setTimeout(() => navigate(ROUTE_PATHS.KONTO, { replace: true }), 2000);
          return;
        }

        setMessage("Nieprawidłowy link aktywacyjny.");
        setStatus("error");
      } catch {
        setMessage("Wystąpił nieoczekiwany błąd. Spróbuj ponownie.");
        setStatus("error");
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "linear-gradient(180deg, oklch(0.07 0.015 235) 0%, oklch(0.10 0.020 225) 100%)" }}
    >
      <div
        className="w-full max-w-sm p-8 rounded-2xl text-center space-y-5"
        style={{
          background: "oklch(0.09 0.018 230 / 0.95)",
          border: "1px solid oklch(0.20 0.03 220)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
        }}
      >
        {status === "loading" && (
          <>
            <Loader2
              className="w-12 h-12 mx-auto animate-spin"
              style={{ color: "rgba(212,175,55,0.7)" }}
            />
            <h2
              className="text-xl font-semibold"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(230,220,200,0.95)" }}
            >
              Weryfikacja konta…
            </h2>
            <p className="text-sm" style={{ color: "rgba(155,170,190,0.7)" }}>
              Proszę czekać, trwa potwierdzanie adresu e-mail.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2
              className="w-12 h-12 mx-auto"
              style={{ color: "rgba(100,200,140,0.85)" }}
            />
            <h2
              className="text-xl font-semibold"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(230,220,200,0.95)" }}
            >
              Konto aktywowane!
            </h2>
            <p className="text-sm" style={{ color: "rgba(155,170,190,0.7)" }}>
              Twój adres e-mail został potwierdzony.<br />
              Za chwilę zostaniesz przekierowany do panelu.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle
              className="w-12 h-12 mx-auto"
              style={{ color: "rgba(220,80,60,0.85)" }}
            />
            <h2
              className="text-xl font-semibold"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(230,220,200,0.95)" }}
            >
              Nie udało się aktywować konta
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(200,170,150,0.8)" }}>
              {message}
            </p>
            <button
              onClick={() => navigate(ROUTE_PATHS.REJESTRACJA, { replace: true })}
              className="mt-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all hover:brightness-110"
              style={{ background: "oklch(0.72 0.14 45)", color: "oklch(0.07 0.01 240)" }}
            >
              Zarejestruj się ponownie
            </button>
          </>
        )}
      </div>
    </div>
  );
}
